// Report and block, wherever a profile is shown
// ---------------------------------------------
// Reporting belongs where you encounter someone, not on a settings page you
// have to go looking for. That means every surface showing another person's
// profile needs these two actions — the peek drawer the feeds open, and the
// full profile page you land on from Heart Games.
//
// Those two surfaces look nothing alike, so they render their own buttons. What
// they must not do is each own a copy of the logic: two implementations of
// "block this person" drift, and the half that drifts is the half nobody is
// testing.
//
// So the behaviour lives here and the presentation stays with the caller.

import { useCallback, useEffect, useState } from 'react';
import { safetyService } from '../../services/Safety/safetyService';
import { ReportType } from '../../types/safety';
import { logger } from '../../utils/logger';

/** The reasons a person can pick, in the words they would use themselves. */
export const REPORT_REASONS: Array<{ type: ReportType; label: string }> = [
  { type: 'harassment', label: 'Harassment or abuse' },
  { type: 'inappropriate_content', label: 'Inappropriate content' },
  { type: 'fake_profile', label: 'Fake profile' },
  { type: 'scam', label: 'Scam or spam' },
  { type: 'underage', label: 'Appears underage' },
  { type: 'other', label: 'Something else' },
];

export interface ProfileSafetyOptions {
  profileId: string | null;
  firstName: string;
  /** Surface-level feedback, e.g. a toast. */
  onNotify?: (type: 'match' | 'request' | 'info' | 'warning', message: string) => void;
  /**
   * This person was blocked. The host should drop them from whatever list it is
   * showing — the block is recorded either way, but a profile that stays on
   * screen after you block it does not look blocked.
   */
  onBlocked?: (profileId: string) => void;
  /** Called after a completed report or block, to dismiss the surface. */
  onDone?: () => void;
}

export function useProfileSafety({
  profileId,
  firstName,
  onNotify,
  onBlocked,
  onDone,
}: ProfileSafetyOptions) {
  const [reporting, setReporting] = useState(false);
  const [reportType, setReportType] = useState<ReportType | null>(null);
  const [submittingReport, setSubmittingReport] = useState(false);
  const [confirmingBlock, setConfirmingBlock] = useState(false);
  const [blocking, setBlocking] = useState(false);

  // A new profile starts from a clean slate. Without this, opening one person
  // straight after half-reporting another would show their name over the
  // previous person's selected reason.
  useEffect(() => {
    setReporting(false);
    setReportType(null);
    setConfirmingBlock(false);
  }, [profileId]);

  const block = useCallback(async () => {
    if (!profileId || blocking) return;
    setBlocking(true);
    try {
      await safetyService.blockUser({ userId: profileId });
      onNotify?.('info', `${firstName} has been blocked.`);
      onBlocked?.(profileId);
      onDone?.();
    } catch (error) {
      logger.error('Failed to block user:', error);
      onNotify?.('warning', "That block couldn't be saved. Please try again.");
    } finally {
      setBlocking(false);
      setConfirmingBlock(false);
    }
  }, [profileId, firstName, blocking, onNotify, onBlocked, onDone]);

  const submitReport = useCallback(async () => {
    if (!profileId || !reportType || submittingReport) return;
    setSubmittingReport(true);
    try {
      await safetyService.reportUser({
        reportedUserId: profileId,
        reportType,
        reason: REPORT_REASONS.find((r) => r.type === reportType)?.label ?? 'Reported from profile',
      });
      onNotify?.('info', 'Thanks — we will review this report.');
      setReporting(false);
      setReportType(null);
      onDone?.();
    } catch (error) {
      logger.error('Failed to report user:', error);
      onNotify?.('warning', "That report couldn't be sent. Please try again.");
    } finally {
      setSubmittingReport(false);
    }
  }, [profileId, reportType, submittingReport, onNotify, onDone]);

  return {
    reporting,
    setReporting,
    reportType,
    setReportType,
    submittingReport,
    confirmingBlock,
    setConfirmingBlock,
    blocking,
    block,
    submitReport,
    reasons: REPORT_REASONS,
  };
}
